import { getFirmwareInfo } from '@/features/SDL/SDLdetail/SDLdetail.utils';
import { safeJsonParse } from '@/utils/json';

const joinCharacter = ', ';

interface GetRowItemProps {
  row: any;
  key: string;
  deviceType?: string;
}

interface GetCustomRowProps {
  row: any;
  deviceType: string;
}

const getRowProductVersion = ({ row }: GetCustomRowProps): string => {
  const products = row?.products || [];
  const firmwareInformation = safeJsonParse(row?.firmware_information);

  // Verify with "data".
  const productKeys = new Map([
    ['Security Center', firmwareInformation?.SecurityCenter?.SecurityCenterInitialRelease],
    ['Omnicast', firmwareInformation?.Omnicast?.OmnInitialRelease],
  ]);

  return products
    .map((product: string) => `${product}: ${productKeys.get(product)}`)
    .join(joinCharacter);
};

const getRowLatestFirmwareSc = ({ row, deviceType }: GetCustomRowProps): string => {
  const firmwareInformation = getFirmwareInfo({
    deviceType,
    rawFirmwareInformation: row?.firmware_information,
  });

  return firmwareInformation?.[0]?.firmware_version || '-';
};
const getRowMinimalFirmware = ({ row }: GetCustomRowProps): string => {
  const firmwareInformation = safeJsonParse(row?.firmware_information);

  return firmwareInformation?.MinimalFirmware;
};
const getRowFirmwareVersionAccessControl = ({ row }: GetCustomRowProps): string => {
  const additionalInformation = safeJsonParse(row?.additional_information);
  const firmwareInformation = safeJsonParse(row?.firmware_information);
  const INTEGRATION_POINT = 'Security Center';

  if (additionalInformation?.IntegrationPoint === INTEGRATION_POINT) {
    return firmwareInformation?.SecurityCenterFirmwareVersion;
  }

  return firmwareInformation?.SynergisFirmwareVersion;
};

const getRowFeatures = ({ row }: GetCustomRowProps): string => {
  const additionalInformation = JSON.parse(row?.additional_information);
  const features = row?.features;

  const PTZ_SUPPORTED = 'PTZ Supported';
  const PTZ_MOTOR_ATTRIBUTE = 'Pan-Tilt';

  const shouldShowPTZ = additionalInformation?.PtzMotorAttributes?.toLowerCase().includes(
    PTZ_MOTOR_ATTRIBUTE.toLowerCase(),
  );

  if (!shouldShowPTZ) {
    return features
      ?.filter((feature: string) => feature.toLowerCase() !== PTZ_SUPPORTED.toLowerCase())
      .join(joinCharacter);
  }

  return Array.isArray(features) ? features.join(joinCharacter) : '';
};

// Verify with "Value list".
const customRows = new Map<string, any>([
  ['products', getRowProductVersion],
  ['latest_firmware', getRowLatestFirmwareSc],
  ['minimum_firmware', getRowMinimalFirmware],
  ['firmware_version__access-control', getRowFirmwareVersionAccessControl],
  ['features', getRowFeatures],
]);

export const getRowItem = ({ row, key, deviceType = '' }: GetRowItemProps): string => {
  const KEY_SEPARATOR = '__';
  const keyComposed = `${key}${KEY_SEPARATOR}${deviceType}`;
  const isCustomRow = customRows.has(keyComposed) || customRows.has(key);

  if (isCustomRow) {
    const resolver = customRows.get(keyComposed) || customRows.get(key);
    return resolver({ row, deviceType }) || '-';
  }

  const rowItem = (row as any)[key];
  const rowItemText = Array.isArray(rowItem) ? rowItem.join(joinCharacter) : rowItem;
  return (rowItemText !== 'null' ? rowItemText : '') || '-';
};

export const wrapData = ({ data }: { data: any[] }) =>
  data.map((record) =>
    Object.keys(record).reduce((prev, curr) => ({ ...prev, [curr]: record[curr]?.raw }), {}),
  );
